import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { 
  getQueries, 
  createQuery, 
  rejectQuery, 
  convertQuery,
  deleteQuery,
  getFunctionalAreas,
  getPriorities,
  analyzeVoice,
  uploadFile,
  toggleQueryVisibility
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Plus, 
  Loader2, 
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  ArrowRight,
  Filter,
  Calendar,
  Mic,
  MicOff,
  Trash2,
  Upload,
  Image,
  X,
  Link as LinkIcon,
  Paperclip,
  HelpCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { QueriesHelpDialog } from '@/components/HelpDialogs';
import DropZone from '@/components/DropZone';
import RichTextEditor from '@/components/RichTextEditor';

const statusConfig = {
  pending: { label: 'Pendiente', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  converted: { label: 'Convertida', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle },
  rejected: { label: 'Rechazada', color: 'bg-slate-100 text-slate-500 border-slate-200', icon: XCircle },
};

const QueriesPage = () => {
  const navigate = useNavigate();
  const { user, canManageTasks, isSuperadmin } = useAuth();
  
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // New query dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newQuery, setNewQuery] = useState({ title: '', description: '', attachments: [], urls: [] });
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  
  // Convert dialog
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [convertForm, setConvertForm] = useState({
    title: '',
    description: '',
    functional_area: 'Otros',
    priority: 'Media',
  });
  const [converting, setConverting] = useState(false);
  
  // Config
  const [areas, setAreas] = useState([]);
  const [priorities, setPriorities] = useState([]);
  
  // Help dialog
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    loadQueries();
    loadConfig();
  }, [filterStatus]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const loadConfig = async () => {
    try {
      const [areasRes, prioritiesRes] = await Promise.all([
        getFunctionalAreas(),
        getPriorities(),
      ]);
      setAreas(areasRes.data.areas);
      setPriorities(prioritiesRes.data.priorities);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadQueries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      
      const response = await getQueries(params);
      setQueries(response.data);
    } catch (error) {
      console.error('Error loading queries:', error);
      toast.error('Error al cargar las consultas');
    } finally {
      setLoading(false);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Detect supported audio format
      let mimeType = 'audio/webm';
      let audioFormat = 'webm';
      if (typeof MediaRecorder.isTypeSupported === 'function') {
        if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm'; audioFormat = 'webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4'; audioFormat = 'mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg'; audioFormat = 'ogg';
        } else {
          mimeType = ''; audioFormat = 'wav';
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorderRef.current._audioFormat = audioFormat;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/wav' });
        await processVoiceRecording(blob, mediaRecorderRef.current._audioFormat);
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      toast.info('Grabando... Habla tu consulta');
    } catch (err) {
      console.error('Error accessing microphone:', err);
      toast.error('No se pudo acceder al microfono. Verifica los permisos.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const processVoiceRecording = async (blob, format = 'webm') => {
    if (blob.size < 1000) {
      toast.error('La grabacion fue demasiado corta. Intenta de nuevo.');
      return;
    }
    setIsProcessingVoice(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result.split(',')[1];
        
        try {
          const response = await analyzeVoice(base64, format);
          const { title, description, raw_transcription } = response.data;
          
          setNewQuery(prev => ({
            ...prev,
            title: title || '',
            description: description || raw_transcription || ''
          }));
          
          toast.success('Audio transcrito correctamente');
        } catch (err) {
          console.error('Error analyzing voice:', err);
          toast.error('Error al procesar el audio');
        } finally {
          setIsProcessingVoice(false);
        }
      };
      reader.onerror = () => {
        toast.error('Error al leer el archivo de audio');
        setIsProcessingVoice(false);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('Error processing audio:', err);
      toast.error('Error al procesar el audio');
      setIsProcessingVoice(false);
    }
  };

  const handleCreateQuery = async () => {
    if (!newQuery.title.trim() || !newQuery.description.trim()) {
      toast.error('Completa todos los campos');
      return;
    }

    setCreating(true);
    try {
      await createQuery(newQuery);
      toast.success('Consulta enviada correctamente');
      setShowNewDialog(false);
      setNewQuery({ title: '', description: '', attachments: [], urls: [] });
      loadQueries();
    } catch (error) {
      console.error('Error creating query:', error);
      toast.error('Error al crear la consulta');
    } finally {
      setCreating(false);
    }
  };

  const handleReject = async (queryId) => {
    try {
      await rejectQuery(queryId);
      toast.success('Consulta rechazada');
      loadQueries();
    } catch (error) {
      console.error('Error rejecting query:', error);
      toast.error('Error al rechazar la consulta');
    }
  };

  const handleDelete = async (queryId) => {
    if (!window.confirm('¿Estas seguro de eliminar esta consulta? Esta accion no se puede deshacer.')) {
      return;
    }
    try {
      await deleteQuery(queryId);
      toast.success('Consulta eliminada');
      loadQueries();
    } catch (error) {
      console.error('Error deleting query:', error);
      toast.error('Error al eliminar la consulta');
    }
  };

  const openConvertDialog = (query) => {
    setSelectedQuery(query);
    setConvertForm({
      title: query.title,
      description: query.description,
      functional_area: 'Otros',
      priority: 'Media',
    });
    setShowConvertDialog(true);
  };

  const handleConvert = async () => {
    if (!convertForm.title.trim()) {
      toast.error('El titulo es requerido');
      return;
    }

    setConverting(true);
    try {
      const response = await convertQuery(selectedQuery.id, convertForm);
      toast.success('Consulta convertida a tarea');
      setShowConvertDialog(false);
      navigate(`/tasks/${response.data.id}`);
    } catch (error) {
      console.error('Error converting query:', error);
      toast.error('Error al convertir la consulta');
    } finally {
      setConverting(false);
    }
  };

  const openNewDialogWithReset = () => {
    setNewQuery({ title: '', description: '', attachments: [], urls: [] });
    setNewUrl('');
    setShowNewDialog(true);
  };

  // File upload for queries
  const handleQueryFileUpload = async (newFiles) => {
    setNewQuery(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newFiles]
    }));
  };

  const handleAddQueryUrl = () => {
    if (!newUrl.trim()) return;
    try {
      new URL(newUrl);
      setNewQuery(prev => ({
        ...prev,
        urls: [...prev.urls, newUrl.trim()]
      }));
      setNewUrl('');
    } catch {
      toast.error('URL no valida');
    }
  };

  const handleRemoveAttachment = (index) => {
    setNewQuery(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleRemoveUrl = (index) => {
    setNewQuery(prev => ({
      ...prev,
      urls: prev.urls.filter((_, i) => i !== index)
    }));
  };

  const handleToggleQueryVisibility = async (queryId) => {
    try {
      await toggleQueryVisibility(queryId);
      loadQueries();
      toast.success('Visibilidad actualizada');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast.error('Error al cambiar visibilidad');
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10" data-testid="queries-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope'] tracking-tight">
                Consultas
              </h1>
              <button
                onClick={() => setShowHelp(true)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="queries-help-btn"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            </div>
            <p className="text-slate-500 mt-1">
              {user?.role === 'staff' ? 'Envia tus consultas al equipo' : 'Consultas del equipo'}
            </p>
          </div>
          <Button 
            onClick={openNewDialogWithReset}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="new-query-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Consulta
          </Button>
        </div>

        {/* Staff only sees create button and a message */}
        {user?.role === 'staff' ? (
          <Card className="bg-white border border-stone-200 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center h-64 text-slate-500">
              <MessageSquare className="w-12 h-12 mb-3 text-slate-300" />
              <p className="text-lg font-medium">Consultas enviadas</p>
              <p className="text-sm">Tus consultas han sido enviadas al equipo para su gestion</p>
            </CardContent>
          </Card>
        ) : (
        <>
        {/* Filter */}
        <Card className="mb-6 bg-white border border-stone-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-slate-400" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[200px] border-stone-200" data-testid="filter-query-status">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="converted">Convertidas</SelectItem>
                  <SelectItem value="rejected">Rechazadas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Queries List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : queries.length === 0 ? (
            <Card className="bg-white border border-stone-200 shadow-sm">
              <CardContent className="flex flex-col items-center justify-center h-64 text-slate-500">
                <MessageSquare className="w-12 h-12 mb-3 text-slate-300" />
                <p className="text-lg font-medium">No hay consultas</p>
                <p className="text-sm">Crea una nueva consulta para empezar</p>
              </CardContent>
            </Card>
          ) : (
            queries.map((query) => {
              const status = statusConfig[query.status];
              const StatusIcon = status.icon;
              
              return (
                <Card 
                  key={query.id} 
                  className="bg-white border border-stone-200 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`query-card-${query.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={`${status.color} border text-[10px] font-bold uppercase`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {status.label}
                          </Badge>
                          {query.created_by_role === 'ugib' && (
                            <Badge className="bg-violet-100 text-violet-700 border border-violet-200 text-[10px] font-bold uppercase">
                              UGIB
                            </Badge>
                          )}
                          <span className="text-sm text-slate-500 flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(query.created_at), 'dd MMM yyyy', { locale: es })}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">{query.title}</h3>
                        <div className="text-slate-600 text-sm line-clamp-2" dangerouslySetInnerHTML={{ __html: query.description }} />
                        {(query.attachments?.length > 0 || query.urls?.length > 0) && (
                          <div className="mt-2 space-y-1">
                            {query.attachments?.map((att, idx) => {
                              const url = typeof att === 'string' ? att : att.url;
                              const filename = typeof att === 'string' ? `Archivo ${idx + 1}` : att.filename;
                              return (
                                <a key={`att-${idx}`} href={url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url}
                                  target="_blank" rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-xs text-emerald-600 hover:underline"
                                >
                                  <Image className="w-3 h-3" />{filename}
                                </a>
                              );
                            })}
                            {query.urls?.map((url, idx) => (
                              <a key={`url-${idx}`} href={url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-xs text-blue-600 hover:underline truncate"
                              >
                                <LinkIcon className="w-3 h-3 flex-shrink-0" />{url}
                              </a>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-2">Por: {query.created_by_name}</p>
                      </div>
                      
                      {canManageTasks() && query.status === 'pending' && (
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReject(query.id)}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            data-testid={`reject-query-${query.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Rechazar
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openConvertDialog(query)}
                            className="bg-emerald-700 hover:bg-emerald-800"
                            data-testid={`convert-query-${query.id}`}
                          >
                            <ArrowRight className="w-4 h-4 mr-1" />
                            Convertir
                          </Button>
                        </div>
                      )}
                      
                      {query.status === 'converted' && query.converted_to_task_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/tasks/${query.converted_to_task_id}`)}
                          className="border-stone-200"
                        >
                          Ver Tarea
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                      
                      {isSuperadmin() && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleQueryVisibility(query.id)}
                          className={query.visible_to_departamentos 
                            ? "text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" 
                            : "text-amber-600 hover:text-amber-700 hover:bg-amber-50"}
                          title={query.visible_to_departamentos ? 'Visible para Departamentos' : 'Oculta para Departamentos'}
                          data-testid={`toggle-visibility-${query.id}`}
                        >
                          {query.visible_to_departamentos ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                      )}

                      {isSuperadmin() && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(query.id)}
                          className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`delete-query-${query.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
        </>
        )}
      </div>

      {/* New Query Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="new-query-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Consulta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Voice Recording Button */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessingVoice}
                data-testid="voice-record-query-btn"
                className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isRecording 
                    ? 'bg-red-600 hover:bg-red-700 animate-pulse shadow-lg shadow-red-600/50' 
                    : isProcessingVoice
                    ? 'bg-slate-400 cursor-not-allowed'
                    : 'bg-emerald-700 hover:bg-emerald-800 shadow-lg'
                }`}
              >
                {isProcessingVoice ? (
                  <Loader2 className="w-7 h-7 text-white animate-spin" />
                ) : isRecording ? (
                  <MicOff className="w-7 h-7 text-white" />
                ) : (
                  <Mic className="w-7 h-7 text-white" />
                )}
              </button>
            </div>
            <p className="text-center text-sm text-slate-500">
              {isProcessingVoice 
                ? 'Procesando audio...' 
                : isRecording 
                ? 'Grabando... Pulsa para detener' 
                : 'Pulsa para grabar tu consulta por voz'}
            </p>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-stone-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-400">o escribe manualmente</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Titulo</Label>
              <Input
                value={newQuery.title}
                onChange={(e) => setNewQuery({ ...newQuery, title: e.target.value })}
                placeholder="Asunto de la consulta"
                className="border-stone-200"
                data-testid="query-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripcion</Label>
              <RichTextEditor
                value={newQuery.description}
                onChange={(val) => setNewQuery({ ...newQuery, description: val })}
                placeholder="Describe tu consulta en detalle..."
                rows={5}
                data-testid="query-description-input"
              />
            </div>

            {/* Attachments Section */}
            <div className="space-y-3 pt-2 border-t border-stone-100">
              <Label className="flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Adjuntos (opcional)
              </Label>
              
              {/* DropZone */}
              <DropZone
                onFilesUploaded={handleQueryFileUpload}
                uploading={uploading}
                setUploading={setUploading}
              />
              
              {/* Uploaded files list */}
              {newQuery.attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {newQuery.attachments.map((att, index) => {
                    const filename = typeof att === 'string' ? `Archivo ${index + 1}` : att.filename;
                    return (
                      <div 
                        key={index}
                        className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full text-sm"
                      >
                        <Image className="w-4 h-4" />
                        <span className="truncate max-w-[200px]">{filename}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index)}
                          className="hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* URL input */}
              <div className="flex gap-2">
                <Input
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="Agregar enlace (https://...)"
                  className="border-stone-200 text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddQueryUrl())}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddQueryUrl}
                  className="border-stone-200"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              {/* URLs list */}
              {newQuery.urls.length > 0 && (
                <div className="space-y-1">
                  {newQuery.urls.map((url, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg text-sm"
                    >
                      <LinkIcon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate flex-1">{url}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveUrl(index)}
                        className="hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateQuery} 
              disabled={creating || isProcessingVoice}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="submit-query-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enviar Consulta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Task Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="convert-query-dialog">
          <DialogHeader>
            <DialogTitle>Convertir a Tarea</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titulo</Label>
              <Input
                value={convertForm.title}
                onChange={(e) => setConvertForm({ ...convertForm, title: e.target.value })}
                className="border-stone-200"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripcion</Label>
              <Textarea
                value={convertForm.description}
                onChange={(e) => setConvertForm({ ...convertForm, description: e.target.value })}
                rows={4}
                className="border-stone-200"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Area Funcional</Label>
                <Select 
                  value={convertForm.functional_area} 
                  onValueChange={(v) => setConvertForm({ ...convertForm, functional_area: v })}
                >
                  <SelectTrigger className="border-stone-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {areas.map((area) => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select 
                  value={convertForm.priority} 
                  onValueChange={(v) => setConvertForm({ ...convertForm, priority: v })}
                >
                  <SelectTrigger className="border-stone-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConvert} 
              disabled={converting}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="confirm-convert-btn"
            >
              {converting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Crear Tarea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Dialog */}
      <QueriesHelpDialog open={showHelp} onClose={() => setShowHelp(false)} />
    </Layout>
  );
};

export default QueriesPage;
